import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchShopJson, SHOP_IMAGE_BASE } from "./shopApi";
import { pruneWishlist, readWishlist, toggleWishlist } from "./wishlistStorage";
import ProductShareModal from "./ProductShareModal";
import "../index.css";

const CATEGORY_RULES = [
  { label: "Rings", patterns: [/\brings?\b/i] },
  { label: "Bracelets", patterns: [/\bbracelets?\b/i] },
  { label: "Necklaces", patterns: [/\bnecklaces?\b/i] },
  { label: "Earrings", patterns: [/\bearrings?\b/i] },
  { label: "Bangles", patterns: [/\bbangles?\b/i] },
  { label: "Pendants", patterns: [/\bpendants?\b/i] },
  { label: "Anklets", patterns: [/\banklets?\b/i] },
  { label: "Chains", patterns: [/\bchains?\b/i] },
];

function getDisplayCategory(product) {
  const source = `${product?.name || ""} ${product?.description || ""}`;

  for (const rule of CATEGORY_RULES) {
    if (rule.patterns.some((pattern) => pattern.test(source))) {
      return rule.label;
    }
  }

  return "Other Finds";
}

function formatPrice(value) {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getKaratLabel(product) {
  const source = `${product?.category_name || ""} ${product?.name || ""}`.toUpperCase();
  const match = source.match(/\b(\d{1,2})\s*K\b/);
  return match ? `${match[1]}K` : "Other";
}

function isVideoUrl(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

export default function ProductGrid() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedBrand, setSelectedBrand] = useState("All Brands");
  const [selectedKarat, setSelectedKarat] = useState("All Karats");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [wishlistIds, setWishlistIds] = useState([]);
  const [visibleCount, setVisibleCount] = useState(20);
  const [loading, setLoading] = useState(true);
  const [shareProduct, setShareProduct] = useState(null);

  useEffect(() => {
    setWishlistIds(readWishlist());
    fetchShopJson("/get_products.php")
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!products.length) {
      return;
    }
    const availableIds = products
      .filter((product) => Number(product.stocks || 0) > 0)
      .map((product) => product.id);
    setWishlistIds(pruneWishlist(availableIds));
  }, [products]);

  const categorizedProducts = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        displayCategory: getDisplayCategory(product),
        displayBrand: (product.brand || "").trim(),
        karatLabel: getKaratLabel(product),
        numericPrice: Number.parseFloat(product.pricePHP || 0),
        numericStocks: Number.parseInt(product.stocks || 0, 10) || 0,
      })),
    [products]
  );

  const categoryItems = useMemo(
    () => [
      { label: "All Categories", count: categorizedProducts.length },
      ...CATEGORY_RULES.map((rule) => ({
        label: rule.label,
        count: categorizedProducts.filter((product) => product.displayCategory === rule.label).length,
      })),
      {
        label: "Other Finds",
        count: categorizedProducts.filter((product) => product.displayCategory === "Other Finds").length,
      },
    ],
    [categorizedProducts]
  );

  const brandOptions = useMemo(() => {
    const brands = new Set();

    categorizedProducts.forEach((product) => {
      if (product.displayBrand) {
        brands.add(product.displayBrand);
      }
    });

    return ["All Brands", ...Array.from(brands).sort((a, b) => a.localeCompare(b))];
  }, [categorizedProducts]);

  const karatOptions = useMemo(() => {
    const karats = new Set();

    categorizedProducts.forEach((product) => {
      if (product.karatLabel && product.karatLabel !== "Other") {
        karats.add(product.karatLabel);
      }
    });

    const sortedKarats = Array.from(karats).sort((a, b) => {
      const numA = Number.parseInt(a, 10);
      const numB = Number.parseInt(b, 10);
      return numA - numB;
    });

    return ["All Karats", ...sortedKarats, "Other"];
  }, [categorizedProducts]);

  const filteredProducts = useMemo(() => {
    const min = minPrice === "" ? null : Number.parseFloat(minPrice);
    const max = maxPrice === "" ? null : Number.parseFloat(maxPrice);

    return categorizedProducts.filter((product) => {
      if (selectedCategory !== "All Categories" && product.displayCategory !== selectedCategory) {
        return false;
      }

      if (selectedBrand !== "All Brands" && product.displayBrand !== selectedBrand) {
        return false;
      }

      if (selectedKarat !== "All Karats" && product.karatLabel !== selectedKarat) {
        return false;
      }

      if (min !== null && !Number.isNaN(min) && product.numericPrice < min) {
        return false;
      }

      if (max !== null && !Number.isNaN(max) && product.numericPrice > max) {
        return false;
      }

      return true;
    });
  }, [categorizedProducts, selectedCategory, selectedBrand, selectedKarat, minPrice, maxPrice]);

  const handleCategorySelect = (categoryLabel) => {
    setSelectedCategory(categoryLabel);
    setVisibleCount(20);
  };

  const resetFilters = () => {
    setSelectedCategory("All Categories");
    setSelectedBrand("All Brands");
    setSelectedKarat("All Karats");
    setMinPrice("");
    setMaxPrice("");
    setVisibleCount(20);
  };

  const openProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleWishlistToggle = (productId) => {
    setWishlistIds(toggleWishlist(productId));
  };

  if (loading) return <p>Loading...</p>;
  if (!products.length) return <p>No products</p>;

  return (
    <>
      <section className="category-section">
        <div className="category-section__copy">
          <p className="category-section__eyebrow">Shop by Category</p>
          <h2>Find the piece you want faster</h2>
          <p>
            Browse the latest collection by jewelry type, karat, brand, and price range in one place.
          </p>
        </div>

        <div className="category-grid">
          {categoryItems.map((category) => (
            <button
              key={category.label}
              type="button"
              className={`category-card ${selectedCategory === category.label ? "is-active" : ""}`}
              onClick={() => handleCategorySelect(category.label)}
            >
              <span className="category-card__label">{category.label}</span>
              <span className="category-card__count">
                {category.count} item{category.count === 1 ? "" : "s"}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="catalog-filters">
        <div className="catalog-filter">
          <label htmlFor="brandFilter">Brand</label>
          <select
            id="brandFilter"
            value={selectedBrand}
            onChange={(e) => {
              setSelectedBrand(e.target.value);
              setVisibleCount(20);
            }}
          >
            {brandOptions.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        <div className="catalog-filter">
          <label htmlFor="karatFilter">Karat Type</label>
          <select
            id="karatFilter"
            value={selectedKarat}
            onChange={(e) => {
              setSelectedKarat(e.target.value);
              setVisibleCount(20);
            }}
          >
            {karatOptions.map((karat) => (
              <option key={karat} value={karat}>
                {karat}
              </option>
            ))}
          </select>
        </div>

        <div className="catalog-filter">
          <label htmlFor="minPrice">Min Price</label>
          <input
            id="minPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={minPrice}
            onChange={(e) => {
              setMinPrice(e.target.value);
              setVisibleCount(20);
            }}
          />
        </div>

        <div className="catalog-filter">
          <label htmlFor="maxPrice">Max Price</label>
          <input
            id="maxPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="999999.99"
            value={maxPrice}
            onChange={(e) => {
              setMaxPrice(e.target.value);
              setVisibleCount(20);
            }}
          />
        </div>

        <button type="button" className="catalog-reset-btn" onClick={resetFilters}>
          Reset Filters
        </button>
      </section>

      <div className="catalog-heading">
        <div>
          <p className="category-section__eyebrow">Catalog</p>
          <h3>{selectedCategory}</h3>
        </div>
        <p className="catalog-heading__summary">
          Showing {Math.min(visibleCount, filteredProducts.length)} of {filteredProducts.length} item
          {filteredProducts.length === 1 ? "" : "s"}
        </p>
      </div>

      <main className="product-grid" id="catalog-grid">
        {filteredProducts.slice(0, visibleCount).map((product) => (
          <article key={product.id} className="product-card">
            <button
              type="button"
              className={`wishlist-toggle ${wishlistIds.includes(String(product.id)) ? "is-active" : ""}`}
              onClick={() => handleWishlistToggle(product.id)}
              aria-label={wishlistIds.includes(String(product.id)) ? "Remove from wishlist" : "Add to wishlist"}
            >
              {wishlistIds.includes(String(product.id)) ? "\u2665" : "\u2661"}
            </button>
            <button
              type="button"
              className="share-toggle"
              onClick={() => setShareProduct(product)}
              aria-label={`Share ${product.name}`}
            >
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path d="M15.5 8.5a3 3 0 1 0-2.74-4.22L8.8 6.47a3 3 0 1 0 0 11.06l3.96 2.19A3 3 0 1 0 14 17.5l-3.97-2.2a3.07 3.07 0 0 0 0-1.6L14 11.5a3 3 0 1 0 1.5-3Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              className="product-card__image-button"
              onClick={() => openProduct(product.id)}
              aria-label={`View details for ${product.name}`}
            >
              {isVideoUrl(product.imageUrl) ? (
                <video src={`${SHOP_IMAGE_BASE}${product.imageUrl}`} muted playsInline preload="metadata" />
              ) : (
                <img src={`${SHOP_IMAGE_BASE}${product.imageUrl}`} alt={product.name} />
              )}
            </button>
            <button
              type="button"
              className="product-card__title-button"
              onClick={() => openProduct(product.id)}
            >
              <h3>{product.name}</h3>
            </button>
            {product.displayBrand && <p className="product-card__brand">{product.displayBrand}</p>}
            <p className="product-card__meta">
              <span>{product.karatLabel}</span>
              <span>{product.numericStocks > 0 ? `${product.numericStocks} in stock` : "No stock"}</span>
            </p>
            <p>PHP {formatPrice(product.pricePHP)}</p>
            <p className="description">{product.description}</p>
          </article>
        ))}
      </main>

      {!filteredProducts.length && (
        <div className="catalog-empty-state">
          No pieces found for the current category, karat, brand, and price range.
        </div>
      )}

      {visibleCount < filteredProducts.length && (
        <div style={{ textAlign: "center", margin: "2rem 0" }}>
          <button className="add-to-cart-btn" onClick={() => setVisibleCount((value) => value + 10)}>
            Show More
          </button>
        </div>
      )}

      {shareProduct && (
        <ProductShareModal
          product={shareProduct}
          onClose={() => setShareProduct(null)}
        />
      )}
    </>
  );
}
